import type {
  SearchKeywordPredicate,
  SearchRangeBound,
  SearchResponseFilterOutput,
} from '@iconik-io/iconik-api/src/services/search';
import type { MetadataFieldType } from '@iconik-io/iconik-api/src/types/metadata_field_type';
import { OBJECT_TYPE_COLLECTIONS } from '@iconik-io/iconik-api/src/types/object_types';
import {
  FIELD_CREATED_BY,
  FIELD_DATE_CREATED,
  FIELD_DATE_MODIFIED,
  FIELD_OBJECT_TYPE,
} from '@iconik-io/shared-lib/src/constants/search';
import type {
  FacetValues,
  FilterValues,
  MultiSelectFilterModifier,
  MultiSelectFilterValue,
} from '@iconik-io/shared-lib/src/redux/common_filters/types';

import { MULTI_SELECT_TEXT_FIELD_TYPES } from '../../constants/search_mappings';
import { STORAGE_ID_TERM_NAMES } from '../../constants/storages';
import {
  mergeStorageGroups,
  STORAGE_CANONICAL_KEY,
  type StorageGroupSource,
} from '../../utils/search_utils/merge_storage_groups';

const METADATA_PREFIX = 'metadata.';

/**
 * The panel renders both `filter.created_by` and `filter.created_by_user` into
 * the same `filters.created_by_user` control, so accept either spelling.
 */
const CREATED_BY_ALIASES = new Set([FIELD_CREATED_BY, 'created_by']);

const DATE_FIELDS = new Set<string>([FIELD_DATE_CREATED, FIELD_DATE_MODIFIED]);

/**
 * `InputDate` cannot render a one-sided range (it needs both `min` and `max`),
 * so an open bound is closed to the widest value that cannot change the
 * meaning of a `date_created`/`date_modified` filter.
 */
const EPOCH = '1970-01-01T00:00:00Z';

/** Metadata field types the panel renders as a single date input. */
const DATE_METADATA_TYPES: ReadonlySet<string> = new Set(['date', 'datetime']);

/** Metadata field types the panel renders as a multi-select with modifiers. */
const MULTI_SELECT_METADATA_TYPES: ReadonlySet<string> = new Set([
  ...MULTI_SELECT_TEXT_FIELD_TYPES,
  'tag_cloud',
  'drop_down',
]);

type MetadataControl = 'multiSelect' | 'boolean' | 'date';

export type NaturalSearchFilterContext = {
  /**
   * Names from `settings.merged.facet_fields`. That list also holds the panel's
   * pseudo-field names (`filter.date_created`, …), but those never collide with
   * a real Elasticsearch field name, so it can be used as-is.
   */
  facetFieldNames: ReadonlySet<string>;
  /**
   * `state.entities.metadata_user_fields`, used only as a fallback when the
   * predicate does not carry a usable `field_type`.
   */
  metadataFields?: Readonly<
    Record<string, { field_type?: MetadataFieldType | string }>
  >;
  /** Injectable clock, so the open-ended date clamp is testable. */
  now?: () => Date;
};

export type NaturalSearchPanelValues = {
  filterValues: FilterValues;
  facetValues: FacetValues;
  /**
   * The applied filters behind each panel key, by the key they set: what a
   * replay sends back for a key the user left as it was projected.
   */
  sourcesByKey: Record<string, SearchResponseFilterOutput[]>;
};

function isRangeBound(value: unknown): value is SearchRangeBound {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    ('gt' in value || 'gte' in value || 'lt' in value || 'lte' in value)
  );
}

/**
 * `kind` is optional on all three predicate variants, so the union is not
 * reliably discriminated — fall back on shape. Lexical (`terms`) and semantic
 * (`text`) predicates are the free-text half of the search, already carried by
 * `filters.query`.
 */
function asKeywordPredicate(
  predicate: SearchResponseFilterOutput['predicate'],
): SearchKeywordPredicate | undefined {
  if ('terms' in predicate || 'text' in predicate) {
    return undefined;
  }
  return 'op' in predicate ? predicate : undefined;
}

/** The redux filter shapes are all string-based, so coerce scalars. */
function toScalarStrings(value: SearchKeywordPredicate['value']): string[] {
  const entries = Array.isArray(value) ? value : [value];
  const result: string[] = [];
  for (const entry of entries) {
    if (isRangeBound(entry)) {
      continue;
    }
    for (const scalar of Array.isArray(entry) ? entry : [entry]) {
      if (scalar === null || scalar === undefined || isRangeBound(scalar)) {
        continue;
      }
      const text = String(scalar);
      if (text && !result.includes(text)) {
        result.push(text);
      }
    }
  }
  return result;
}

function firstRangeBound(
  value: SearchKeywordPredicate['value'],
): SearchRangeBound | undefined {
  if (isRangeBound(value)) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.find(isRangeBound);
  }
  return undefined;
}

function toModifier(
  predicate: SearchKeywordPredicate,
): MultiSelectFilterModifier | undefined {
  const prefix = predicate.negate ? 'exclude' : 'include';
  switch (predicate.op) {
    case 'all':
      return `${prefix}_all_of`;
    // `*_any_of` for a single value: the two match the same assets, but several
    // controls (single-select drop-downs, users) only offer "any of", and a value
    // the user then adds to it should widen the filter rather than narrow it.
    case 'eq':
    case 'in':
    case 'any_of':
      return `${prefix}_any_of`;
    default:
      // `range` and `exists` never reach a multi-select control.
      return undefined;
  }
}

function toMultiSelectValue(
  predicate: SearchKeywordPredicate,
): MultiSelectFilterValue | undefined {
  const modifier = toModifier(predicate);
  if (!modifier) {
    return undefined;
  }
  const items = toScalarStrings(predicate.value);
  return items.length > 0 ? [{ modifier, items }] : undefined;
}

/**
 * Resolve a `{gte|gt, lte|lt}` bound to the `{min, max}` the date controls
 * read. Exclusive bounds are treated as inclusive: the control has no notion of
 * exclusivity and the value is display-only until the user edits it.
 */
function toDateRange(
  predicate: SearchKeywordPredicate,
  now: () => Date,
): { min: string; max: string } | undefined {
  if (predicate.negate) {
    return undefined;
  }

  if (predicate.op === 'eq') {
    const [value] = toScalarStrings(predicate.value);
    return value ? { min: value, max: value } : undefined;
  }

  if (predicate.op !== 'range') {
    return undefined;
  }

  const bound = firstRangeBound(predicate.value);
  if (!bound) {
    return undefined;
  }
  const min = bound.gte ?? bound.gt;
  const max = bound.lte ?? bound.lt;
  if (
    (min === null || min === undefined) &&
    (max === null || max === undefined)
  ) {
    return undefined;
  }

  // Closing an open bound is what makes the field render at all; see EPOCH.
  return {
    min: min === null || min === undefined ? EPOCH : String(min),
    max: max === null || max === undefined ? now().toISOString() : String(max),
  };
}

/**
 * A metadata date/datetime control holds a single date, not a range, so it can
 * only carry one bound. Prefer the lower bound, fall back to the upper — never
 * an epoch/now sentinel, which (unlike the two-field system date control) would
 * be shown to the user as if it were the real constraint.
 */
function toMetadataDateValue(
  predicate: SearchKeywordPredicate,
): string | undefined {
  if (predicate.negate) {
    return undefined;
  }
  if (predicate.op === 'eq') {
    const [value] = toScalarStrings(predicate.value);
    return value || undefined;
  }
  if (predicate.op !== 'range') {
    return undefined;
  }
  const bound = firstRangeBound(predicate.value);
  if (!bound) {
    return undefined;
  }
  const chosen = bound.gte ?? bound.gt ?? bound.lte ?? bound.lt;
  return chosen === null || chosen === undefined ? undefined : String(chosen);
}

function resolveMetadataControl(
  fieldName: string,
  predicate: SearchResponseFilterOutput,
  keyword: SearchKeywordPredicate,
  context: NaturalSearchFilterContext,
): MetadataControl {
  // The orchestrator resolved the field against the Elasticsearch mapping, and
  // `metadata_user_fields` is lazily loaded and often empty, so prefer the
  // predicate's own type.
  const fieldType =
    predicate.field_type ?? context.metadataFields?.[fieldName]?.field_type;

  if (fieldType === 'boolean') {
    return 'boolean';
  }
  if (fieldType && DATE_METADATA_TYPES.has(fieldType)) {
    return 'date';
  }
  if (fieldType && MULTI_SELECT_METADATA_TYPES.has(fieldType)) {
    return 'multiSelect';
  }

  // An unknown or raw Elasticsearch type (`keyword`, `long`, …): infer from the
  // predicate shape. Multi-select covers all but two of the in-scope types, and
  // a wrong guess is dropped by the panel's guards rather than corrupting state.
  return keyword.op === 'range' ? 'date' : 'multiSelect';
}

function appendMultiSelect(
  filterValues: FilterValues,
  key: string,
  value: MultiSelectFilterValue,
): void {
  const existing = filterValues[key];
  // Several predicates on one field all hold (`disposition: 'mandatory'`), and
  // groups AND together at the top level, so append rather than replace.
  // The stored value is a union of every filter shape, and only a multi-select
  // one can be appended to another.
  filterValues[key] = Array.isArray(existing)
    ? // biome-ignore lint/plugin: see above.
      [...(existing as MultiSelectFilterValue), ...value]
    : value;
}

function appendFacetValues(
  facetValues: FacetValues,
  field: string,
  items: string[],
): void {
  const existing = facetValues[field] ?? [];
  facetValues[field] = [
    ...existing,
    ...items.filter((item) => !existing.includes(item)),
  ];
}

/**
 * The words a natural search applied, as the panel shows its query: the query's
 * lexical and semantic filters, not the whole question the user asked, whose
 * other parts became the filters around them. Empty when it applied none, as
 * for a browse by filters.
 */
export function naturalFiltersToQuery(
  applied: SearchResponseFilterOutput[] | undefined,
): string {
  const words: string[] = [];
  for (const { predicate } of applied ?? []) {
    const text =
      'terms' in predicate
        ? predicate.terms || predicate.any_of?.join(' ')
        : 'text' in predicate
          ? predicate.text
          : undefined;
    const trimmed = text?.trim();
    if (trimmed && !words.includes(trimmed)) {
      words.push(trimmed);
    }
  }
  return words.join(' ');
}

/**
 * Project the filters the orchestrator applied (`filters.applied` on a
 * `POST /v2/search/natural` response) onto the search filters panel's redux
 * state, so the user can see and adjust what the AI decided.
 *
 * Only fields the panel has a control for are mapped, and only filters that
 * scoped the results (not ranking signals); everything else is dropped, including `status` (which drives the regular search's own default),
 * fields with no control (`id`, `external_id`, `file_names`, `files.path`,
 * `duration_milliseconds`, `transcription_text`), the collection-scoped
 * `in_collections`/`ancestor_collections`, `op: 'exists'` predicates, and
 * negated values on facet fields (a checkbox cannot express exclusion).
 */
export function naturalFiltersToPanelValues(
  applied: SearchResponseFilterOutput[] | undefined,
  context: NaturalSearchFilterContext,
): NaturalSearchPanelValues {
  const filterValues: FilterValues = {};
  const facetValues: FacetValues = {};
  const sourcesByKey: Record<string, SearchResponseFilterOutput[]> = {};
  const storageGroups: StorageGroupSource[] = [];
  const now = context.now ?? (() => new Date());
  const addSource = (key: string, source: SearchResponseFilterOutput) => {
    sourcesByKey[key] = [...(sourcesByKey[key] ?? []), source];
  };

  for (const fieldPredicate of applied ?? []) {
    // A ranking signal did not scope the results, so it is not shown among
    // the filters that did.
    if (fieldPredicate.role === 'scoring') {
      continue;
    }
    const field = fieldPredicate.field;
    const keyword = asKeywordPredicate(fieldPredicate.predicate);
    if (!field || !keyword || keyword.op === 'exists') {
      continue;
    }

    if (DATE_FIELDS.has(field)) {
      const range = toDateRange(keyword, now);
      if (range) {
        filterValues[field] = range;
        // The control shows the last range only, but a replay of it untouched
        // must still send every one it stands for.
        addSource(field, fieldPredicate);
      }
      continue;
    }

    if (CREATED_BY_ALIASES.has(field)) {
      const value = toMultiSelectValue(keyword);
      if (value) {
        appendMultiSelect(filterValues, FIELD_CREATED_BY, value);
        addSource(FIELD_CREATED_BY, fieldPredicate);
      }
      continue;
    }

    if (STORAGE_ID_TERM_NAMES.has(field) || field === STORAGE_CANONICAL_KEY) {
      const value = toMultiSelectValue(keyword);
      if (value) {
        storageGroups.push({ nestedKey: field, groups: value });
        addSource(STORAGE_CANONICAL_KEY, fieldPredicate);
      }
      continue;
    }

    if (field.startsWith(METADATA_PREFIX)) {
      const fieldName = field.slice(METADATA_PREFIX.length);
      const control = resolveMetadataControl(
        fieldName,
        fieldPredicate,
        keyword,
        context,
      );

      if (control === 'multiSelect') {
        const value = toMultiSelectValue(keyword);
        if (value) {
          appendMultiSelect(filterValues, field, value);
          addSource(field, fieldPredicate);
        }
        continue;
      }

      if (control === 'boolean') {
        // The panel's boolean select is backed by the strings 'true'/'false'.
        const [value] = toScalarStrings(keyword.value);
        if (!keyword.negate && (value === 'true' || value === 'false')) {
          filterValues[field] = value;
          addSource(field, fieldPredicate);
        }
        continue;
      }

      // A metadata date control holds a single date; store the one bound it
      // can represent rather than a clamped range's lower edge.
      const dateValue = toMetadataDateValue(keyword);
      if (dateValue) {
        filterValues[field] = dateValue;
        addSource(field, fieldPredicate);
      }
      continue;
    }

    if (context.facetFieldNames.has(field)) {
      // A checkbox group cannot express exclusion, and a range or existence
      // check has no checkbox at all. An `all` predicate is shown as a plain
      // checked list — checkboxes are OR-only, so the semantics drift to OR if
      // the user then edits the filter and re-searches.
      if (keyword.negate || !toModifier(keyword)) {
        continue;
      }
      const items = toScalarStrings(keyword.value);
      if (items.length > 0) {
        appendFacetValues(facetValues, field, items);
        addSource(field, fieldPredicate);
      }
    }
  }

  if (storageGroups.length > 0) {
    const objectTypes = facetValues[FIELD_OBJECT_TYPE];
    const onlyCollectionsSelected =
      objectTypes?.length === 1 && objectTypes[0] === OBJECT_TYPE_COLLECTIONS;
    filterValues[STORAGE_CANONICAL_KEY] = mergeStorageGroups(
      storageGroups,
      onlyCollectionsSelected,
    );
  }

  return { filterValues, facetValues, sourcesByKey };
}
